import React, { useState } from 'react'
import { UserInfo } from '../../api/dots/getUserInfo'
// tslint:disable-next-line no-submodule-imports
import {
  Page,
  Document,
  Text,
  Image,
  StyleSheet,
  View,
} from '@react-pdf/renderer'
import moment from 'moment'
import { GetLogFromTrackingNumberResponse } from '../../api/dots/getLogFromTrackingNumber'

export const getPropertyTypeName = (propertyType: PropertyType | null) => {
  if (propertyType === PropertyType.Erf) {
    return 'Erf'
  }
  if (propertyType === PropertyType.Farm) {
    return 'Farm'
  }
  if (propertyType === PropertyType.AgriculturalHolding) {
    return 'Agricultural Holding'
  }
  if (propertyType === PropertyType.SectionalTitleUnit) {
    return 'Sectional Title Unit'
  }
  return 'Unknown'
}

const tableTitleBackground = '#0078a4'
const rowAndCellPadding = 3
const tableHeaderBackground = '#79b4c9'
const border = '1px solid lightgrey'
const styles = StyleSheet.create({
  page: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'flex-start',
    alignItems: 'center',
    fontFamily: 'Helvetica',
    flex: 1,
    fontSize: 11,
    paddingTop: 30,
    paddingLeft: 60,
    paddingRight: 60,
    lineHeight: 1.5,
  },
  logo: {
    width: 120,
    height: 61,
  },
  header: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: '20px',
    marginBottom: 20,
    border,
  },
  headerContent: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'flex-start',
    alignItems: 'center',
    paddingTop: 10,
    paddingBottom: 10,
  },
  headeTitle: {
    fontSize: 16,
    fontWeight: 'bold',
    textAlign: 'center',
  },
  headerSubTitle: {
    fontSize: 12,
    textAlign: 'center',
  },
  innerContent: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'stretch',
    marginTop: 10,
    width: 300,
  },
  innerSection1: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'flex-start',
    alignItems: 'flex-start',
  },
  innerSection2: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'flex-start',
    alignItems: 'flex-end',
  },
  row: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'flex-end',
    alignItems: 'center',
  },
  label: {
    fontSize: 12,
    fontWeight: 'black',
  },
  erfEnquiry: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'flex-start',
    alignItems: 'stretch',
    width: 580,
    border,
    marginBottom: 10,
  },
  selectedProperty: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'flex-start',
    alignItems: 'stretch',
    width: 580,
    marginBottom: 10,
    border,
  },
  propertyTitleCell: {
    padding: rowAndCellPadding,
    backgroundColor: 'grey',
    color: 'white',
    border,
  },
  propertyTitleRow: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'stretch',
    width: 580,
    padding: rowAndCellPadding,
    border,
    backgroundColor: tableTitleBackground,
  },
  propertyRow: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'stretch',
    width: 580,
  },
  propertyRowHeader: {
    padding: rowAndCellPadding,
    backgroundColor: tableHeaderBackground,
    flex: 0.5,
    border,
  },
  propertyRowCell: {
    padding: rowAndCellPadding,
    flex: 1,
    border,
  },
  propertyTitle: {
    fontSize: 14,
  },
})

export enum PropertyType {
  Erf = 'E',
  Farm = 'F',
  AgriculturalHolding = 'H',
  SectionalTitleUnit = 'U',
}

interface DotsReportProps {
  userInfo: UserInfo | undefined
  selectedProperty: {
    propertyType: PropertyType | null
    officeCode: string
    parcelNumber: string
    portionNumber: string
    sectionNumber: string
    schemeName: string
    allotment: string
  }
  reference?: string
  requestedDate: string
  statuses: GetLogFromTrackingNumberResponse
}

export const DotsReport = (props: DotsReportProps) => {
  const { statuses, selectedProperty, requestedDate, reference, userInfo } =
    props

  const imgUrl = require('../../assets/1000.png')

  const {
    propertyType,
    officeCode,
    parcelNumber,
    portionNumber,
    sectionNumber,
    schemeName,
    allotment,
  } = selectedProperty

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        <View style={styles.header}>
          <Image source={imgUrl} style={styles.logo} />
          <View style={styles.headerContent}>
            <Text style={styles.headeTitle}>CMA Info - Calremont</Text>
            <Text style={styles.headerSubTitle}>
              {userInfo?.firstName || ''} {userInfo?.surname || ''}
            </Text>
            <View style={styles.innerContent}>
              <View style={styles.innerSection1}>
                <View style={styles.row}>
                  <Text style={styles.label}>Email: </Text>
                  <Text>{userInfo?.email || ''}</Text>
                </View>
                <View style={styles.row}>
                  <Text style={styles.label}>Cell: </Text>
                  <Text>{userInfo?.cell || ''}</Text>
                </View>
              </View>
              <View style={styles.innerSection2}>
                {userInfo?.telephone && (
                  <View style={styles.row}>
                    <Text style={styles.label}>Tel: </Text>
                    <Text>{userInfo?.telephone}</Text>
                  </View>
                )}
                {userInfo?.fax && (
                  <View style={styles.row}>
                    <Text style={styles.label}>Fax: </Text>
                    <Text>{userInfo?.fax}</Text>
                  </View>
                )}
              </View>
            </View>
          </View>
          <Image source={imgUrl} style={styles.logo} />
        </View>
        <View style={styles.erfEnquiry}>
          <View style={styles.propertyTitleRow}>
            <Text>Erf Enquiry</Text>
          </View>
          <View
            style={[
              styles.propertyRow,
              { backgroundColor: tableHeaderBackground, padding: 0 },
            ]}
          >
            <View style={styles.propertyRowCell}>
              <Text>Requested</Text>
            </View>
            <View style={styles.propertyRowCell}>
              <Text>Deeds Registry</Text>
            </View>
            <View style={styles.propertyRowCell}>
              <Text>Reference</Text>
            </View>
          </View>
          <View style={styles.propertyRow}>
            <View style={styles.propertyRowCell}>
              <Text>{requestedDate}</Text>
            </View>
            <View style={styles.propertyRowCell}>
              <Text>{officeCode}</Text>
            </View>
            <View style={styles.propertyRowCell}>
              <Text>{reference}</Text>
            </View>
          </View>
        </View>
        <View style={styles.selectedProperty}>
          <View style={styles.propertyTitleRow}>
            <Text>{}</Text>
          </View>

          <Row>
            <RowHeader>Property Type</RowHeader>
            <View style={styles.propertyRowCell}>
              <Text>{getPropertyTypeName(propertyType)}</Text>
            </View>
          </Row>

          <Row>
            <RowHeader>Township</RowHeader>
            <View style={styles.propertyRowCell}>
              <Text>{allotment}</Text>
            </View>
          </Row>

          <Row>
            <RowHeader>Erf Number</RowHeader>
            <View style={styles.propertyRowCell}>
              <Text>{parcelNumber}</Text>
            </View>
          </Row>

          <Row>
            <RowHeader>Portion</RowHeader>
            <View style={styles.propertyRowCell}>
              <Text>{portionNumber}</Text>
            </View>
          </Row>

          <Row>
            <RowHeader>Province</RowHeader>
            <View style={styles.propertyRowCell}>
              <Text>Western Cape</Text>
            </View>
          </Row>
        </View>

        <View style={styles.selectedProperty}>
          <View style={styles.propertyTitleRow}>
            <Text>Tracking Status</Text>
          </View>
          <View
            style={[
              styles.propertyRow,
              { backgroundColor: tableHeaderBackground, padding: 0 },
            ]}
          >
            <View style={styles.propertyRowCell}>
              <Text>Status</Text>
            </View>
            <View style={styles.propertyRowCell}>
              <Text>Date</Text>
            </View>
          </View>

          {statuses.map((status, index) => (
            <View style={styles.propertyRow} key={status.scannedDate}>
              <View style={styles.propertyRowCell}>
                <Text>{status.trackingStatus}</Text>
              </View>
              <View style={styles.propertyRowCell}>
                <Text>{status.scannedDate}</Text>
              </View>
            </View>
          ))}
        </View>
      </Page>
    </Document>
  )
}

const RowHeader = (props: { children: React.ReactNode }) => {
  return (
    <View style={styles.propertyRowHeader}>
      <Text>{props.children}</Text>
    </View>
  )
}

const Row = (props: { children: React.ReactNode }) => {
  return <View style={styles.propertyRow}>{props.children}</View>
}
